import { useCallback, useEffect, useRef, useState } from "react";

/** What counts as being at the keyboard. */
const ACTIVITY = ["pointerdown", "keydown", "wheel", "touchstart"] as const;

/** How long the warning stands before the session ends. */
export const IDLE_WARNING_SECONDS = 10;

/**
 * Seconds until an idle session ends, counted only inside the last few.
 *
 * Returns `null` while there is nothing to say — which is almost always — and
 * a number once the warning is due, so the caller can put a countdown on
 * screen. `onIdle` fires once, when it reaches zero.
 *
 * One interval reading a timestamp rather than a timer reset per event: the
 * activity events fire hundreds of times a minute and only need to write a
 * number. A null or zero limit means no timeout, which is what an unset policy
 * resolves to.
 *
 * The timer is per tab — working in one tab does not hold another awake — and
 * a reload starts it over. Both are what a person would expect of a screen
 * that locks itself.
 */
export function useIdleTimeout(
  minutes: number | null | undefined,
  onIdle: () => void,
): { secondsLeft: number | null; stay: () => void } {
  // Held in a ref so a caller that rebuilds its handler every render does not
  // restart the countdown. Written in an effect, not during render.
  const latest = useRef(onIdle);
  useEffect(() => {
    latest.current = onIdle;
  });

  const lastActivity = useRef(Date.now());
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);

  const stay = useCallback(() => {
    lastActivity.current = Date.now();
    setSecondsLeft(null);
  }, []);

  useEffect(() => {
    if (!minutes || minutes <= 0) {
      setSecondsLeft(null);
      return;
    }
    const limit = minutes * 60_000;
    lastActivity.current = Date.now();
    let done = false;
    const touch = () => {
      lastActivity.current = Date.now();
    };

    for (const event of ACTIVITY) document.addEventListener(event, touch, { passive: true });
    // Every second, because the last ten of them are counted out loud.
    const id = setInterval(() => {
      if (done) return;
      const left = Math.ceil((lastActivity.current + limit - Date.now()) / 1000);
      if (left <= 0) {
        done = true;
        setSecondsLeft(null);
        latest.current();
        return;
      }
      setSecondsLeft(left <= IDLE_WARNING_SECONDS ? left : null);
    }, 1000);

    return () => {
      clearInterval(id);
      for (const event of ACTIVITY) document.removeEventListener(event, touch);
    };
  }, [minutes]);

  return { secondsLeft, stay };
}
